import type { Object as PortalObject } from '@gmn-portal/backend';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeepPartial } from 'ts-essentials';
import { payloadClient } from '.';
import type { BackendActivityItem, FindPayloadApiClientParams } from './types';

type PaginatedDocs<T = any> = {
  docs: T[];
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage?: null | number | undefined;
  page?: number;
  pagingCounter: number;
  prevPage?: null | number | undefined;
  totalDocs: number;
  totalPages: number;
};

// Query keys for better type safety and reusability
export const objectKeys = {
  all: ['objects'] as const,
  lists: () => [...objectKeys.all, 'list'] as const,
  list: (params: ObjectQueryParams) => [...objectKeys.lists(), params] as const,
  overview: (params: ObjectsOverviewQueryParams) =>
    [...objectKeys.all, 'overview', params] as const,
  details: () => [...objectKeys.all, 'detail'] as const,
  detail: (id: string) => [...objectKeys.details(), id] as const,
  versions: () => [...objectKeys.all, 'versions'] as const,
  version: (id: string) => [...objectKeys.versions(), id] as const,
} as const;

export type ObjectQueryParams = FindPayloadApiClientParams<'objects', PortalObject> & {
  disabled?: boolean;
};

export type ObjectsOverviewQueryParams = {
  page: number;
  limit: number;
  category: string;
  sort?: string;
  searchTerm?: string;
  assignedCustomerId?: string | null;
  metadataFilters?: Record<string, unknown>;
  relationFilters?: Record<string, string>;
  disabled?: boolean;
};

export const useObjects = (params?: ObjectQueryParams) => {
  return useQuery({
    queryKey: objectKeys.list(params ?? {}),
    enabled: params?.disabled !== true,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await payloadClient.find({
        collection: 'objects',
        ...params,
      });
      return response;
    },
  });
};

export const useObjectsOverview = (params: ObjectsOverviewQueryParams) => {
  return useQuery({
    queryKey: objectKeys.overview(params),
    enabled: params.disabled !== true,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const response = await payloadClient.customRequest<PaginatedDocs<PortalObject>>({
        subpath: '/objects/overview',
        method: 'POST',
        data: {
          page: params.page,
          limit: params.limit,
          sort: params.sort,
          category: params.category,
          searchTerm: params.searchTerm,
          assignedCustomerId: params.assignedCustomerId,
          metadataFilters: params.metadataFilters,
          relationFilters: params.relationFilters,
        },
      });
      return response;
    },
  });
};

export const useObject = (id: string, params?: { disabled?: boolean }) => {
  return useQuery({
    queryKey: objectKeys.detail(id),
    enabled: !params?.disabled && !!id,
    queryFn: async () => {
      const response = await payloadClient.findById({
        collection: 'objects',
        id,
      });
      return response;
    },
  });
};

export const useObjectVersions = (
  id: string,
  params?: { disabled?: boolean; limit?: number; page?: number }
) => {
  interface ActivityFeedResponse {
    docs: BackendActivityItem[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  }

  return useInfiniteQuery<ActivityFeedResponse>({
    queryKey: objectKeys.version(id),
    enabled: !params?.disabled && !!id,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    queryFn: async ({ pageParam = 1 }) => {
      const response = await payloadClient.customRequest<ActivityFeedResponse>({
        subpath: `/objects/${id}/activity_feed?limit=${params?.limit || 15}&page=${pageParam}`,
        method: 'GET',
      });
      return response;
    },
  });
};

export const useCreateObject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: DeepPartial<PortalObject>) => {
      const response = await payloadClient.create({
        collection: 'objects',
        data,
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: objectKeys.all });
    },
  });
};

export const useUpdateObject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: DeepPartial<PortalObject> }) => {
      const response = await payloadClient.updateById({
        collection: 'objects',
        id,
        data,
      });
      return response;
    },
    onSuccess: (_, { id }) => {
      // Invalidate all objects queries
      queryClient.invalidateQueries({ queryKey: objectKeys.all });
      // Invalidate the specific object query
      queryClient.invalidateQueries({ queryKey: objectKeys.detail(id) });
    },
  });
};

export const useDeleteObject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await payloadClient.deleteById({
        collection: 'objects',
        id,
      });
      return response;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: objectKeys.all });
      // Invalidate the specific object query
      queryClient.invalidateQueries({ queryKey: objectKeys.detail(id) });
    },
  });
};
